import { useMemo, useState, useEffect } from 'react';
import { AttendanceSheetStatusEnum } from '../../enums/attendance-sheet-status.enum';
import { notificationsSummaryByIds, sendMassNotification } from '../../attendance-sheet.service';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { Modal, Button, ProgressBar } from 'react-bootstrap';
import { ListAllIntershipsOfTheMonthResponseDto } from '@/api.types';
import { NotificationsSummaryRow } from '../../attendance-sheet.service';

interface MassNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  registrosVisiveis: ListAllIntershipsOfTheMonthResponseDto[];
}

type LinhaModal = {
  folhaId: string;
  estagiarioNome: string;
  status: number;
  notificacoesQtd: number;
  ultimaNotificacaoEm: string | null;
};

export function MassNotificationModal({ isOpen, onClose, registrosVisiveis }: MassNotificationModalProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [enviados, setEnviados] = useState<string[]>([]);
  const [erros, setErros] = useState<{ id: string, mensagem: string }[]>([]);
  const [linhas, setLinhas] = useState<LinhaModal[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [progressoEnvio, setProgressoEnvio] = useState(0);
  const [totalParaEnviar, setTotalParaEnviar] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    setCarregando(true);
    const folhasVisiveis = registrosVisiveis
      .filter((r) => !!r.folha?.id)
      .map((r) => ({
        folhaId: r.folha!.id,
        estagiarioNome: r.estagiario.nome,
        status: r.folha!.status,
      }));

    const uniqueByFolhaId = Array.from(new Map(folhasVisiveis.map((r) => [r.folhaId, r])).values());
    const folhaIds = uniqueByFolhaId.map((r) => r.folhaId);

    notificationsSummaryByIds(folhaIds)
      .then((summaryRows: NotificationsSummaryRow[] | any) => {
        const summaryMap = new Map<string, NotificationsSummaryRow>();
        (summaryRows ?? []).forEach((s: NotificationsSummaryRow) => summaryMap.set(s.folhaId, s));

        setLinhas(uniqueByFolhaId.map((r) => {
          const s = summaryMap.get(r.folhaId);
          return {
            folhaId: r.folhaId,
            estagiarioNome: r.estagiarioNome,
            status: r.status,
            notificacoesQtd: s?.notificacoesQtd ?? 0,
            ultimaNotificacaoEm: s?.ultimaNotificacaoEm ?? null,
          };
        }));
      })
      .catch(() => toast.error('Erro ao carregar notificações'))
      .finally(() => setCarregando(false));
  }, [isOpen, registrosVisiveis]);

  const registrosNotificaveis = useMemo(() => (
    linhas.filter((row) =>
      row.status === AttendanceSheetStatusEnum.EM_PREENCHIMENTO ||
      row.status === AttendanceSheetStatusEnum.EM_CORRECAO
    )
  ), [linhas]);

  const handleCheckboxChange = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(item => item !== id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const folhasNotificaveis = registrosNotificaveis.map((r) => r.folhaId);

      setSelectedIds(folhasNotificaveis);
    } else {
      setSelectedIds([]);
    }
  };

  const sendNotifications = async () => {
    if (selectedIds.length === 0) return;

    setIsSending(true);
    setEnviados([]);
    setErros([]);
    setProgressoEnvio(0);
    setTotalParaEnviar(selectedIds.length);

    try {
      // Função modificada para mostrar progresso
      const result = await sendMassNotification(selectedIds, (progresso) => {
        setProgressoEnvio(progresso);
      });

      // Atualiza os estados com os resultados
      setEnviados(result.success.map(s => s.split(':')[0].replace('Folha ', '').trim()));

      const novosErros = result.errors.map(error => {
        const match = error.match(/Folha (.+?):/);
        return {
          id: match ? match[1] : 'unknown',
          mensagem: error
        };
      });
      setErros(novosErros);
      setCarregando(true);
      const folhasVisiveis = registrosVisiveis
        .filter((r) => !!r.folha?.id)
        .map((r) => ({
          folhaId: r.folha!.id,
          estagiarioNome: r.estagiario.nome,
          status: r.folha!.status,
        }));
      const uniqueByFolhaId = Array.from(new Map(folhasVisiveis.map((r) => [r.folhaId, r])).values());
      const folhaIds = uniqueByFolhaId.map((r) => r.folhaId);

      await notificationsSummaryByIds(folhaIds)
        .then((summaryRows: NotificationsSummaryRow[] | any) => {
          const summaryMap = new Map<string, NotificationsSummaryRow>();
          (summaryRows ?? []).forEach((s: NotificationsSummaryRow) => summaryMap.set(s.folhaId, s));
          setLinhas(uniqueByFolhaId.map((r) => {
            const s = summaryMap.get(r.folhaId);
            return {
              folhaId: r.folhaId,
              estagiarioNome: r.estagiarioNome,
              status: r.status,
              notificacoesQtd: s?.notificacoesQtd ?? 0,
              ultimaNotificacaoEm: s?.ultimaNotificacaoEm ?? null,
            };
          }));
        })
        .finally(() => setCarregando(false));

      if (result.success.length > 0) {
        toast.success(`${result.success.length} notificação(ões) enviada(s) com sucesso`);
      }
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} notificação(ões) falharam`);
      }

    } catch (error) {
      toast.error('Erro ao processar notificações em massa');
    } finally {
      setIsSending(false);
      setProgressoEnvio(0);
      setSelectedIds([]);
    }
  };

  const porcentagemProgresso = totalParaEnviar > 0
    ? Math.round((progressoEnvio / totalParaEnviar) * 100)
    : 0;

  return (
    <Modal show={isOpen} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Notificação em Massa</Modal.Title>
      </Modal.Header>

      {isSending && (
        <div className="px-3 pt-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="text-muted">
              Enviando notificações... {progressoEnvio} de {totalParaEnviar}
            </small>
            <small className="text-muted">{porcentagemProgresso}%</small>
          </div>
          <ProgressBar
            now={porcentagemProgresso}
            animated
            striped
            variant="primary"
            style={{ height: '8px' }}
          />
        </div>
      )}

      <Modal.Body>
        {(enviados.length > 0 || erros.length > 0) && (
          <div className="alert alert-info mb-3">
            <strong>Resultado do envio:</strong>
            {enviados.length > 0 && <span className="text-success ms-2">✅ {enviados.length} enviadas</span>}
            {erros.length > 0 && <span className="text-danger ms-2">❌ {erros.length} falhas</span>}
          </div>
        )}

        <div className="form-check mb-3">
          <input
            type="checkbox"
            className="form-check-input"
            id="selectAll"
            onChange={(e) => handleSelectAll(e.target.checked)}
            checked={selectedIds.length === registrosNotificaveis.length && registrosNotificaveis.length > 0}
            disabled={registrosNotificaveis.length === 0 || isSending || carregando}
          />
          <label className="form-check-label" htmlFor="selectAll">
            Selecionar Todos ({registrosNotificaveis.length} notificáveis)
          </label>
        </div>

        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Selecionar</th>
                <th>Nome</th>
                <th>Notificações</th>
                <th>Última Notificação</th>
                <th>Status</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((registro) => {
                const podeNotificar =
                  registro.status === AttendanceSheetStatusEnum.EM_PREENCHIMENTO ||
                  registro.status === AttendanceSheetStatusEnum.EM_CORRECAO;

                const foiEnviado = enviados.includes(registro.folhaId);
                const erro = erros.find(e => e.id === registro.folhaId);

                return (
                  <tr key={registro.folhaId} className={!podeNotificar ? 'text-muted' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(registro.folhaId)}
                          onChange={(e) => handleCheckboxChange(registro.folhaId, e.target.checked)}
                          disabled={!podeNotificar || isSending || carregando}
                        />
                      </td>
                      <td>{registro.estagiarioNome}</td>
                      <td>
                        <span className="badge bg-info">
                          {registro.notificacoesQtd}
                        </span>
                      </td>
                      <td>
                        {registro.ultimaNotificacaoEm
                          ? format(new Date(registro.ultimaNotificacaoEm), 'dd/MM/yyyy HH:mm')
                          : 'Nunca'
                        }
                      </td>
                      <td>
                        <span className={`badge ${registro.status === AttendanceSheetStatusEnum.EM_PREENCHIMENTO ? 'bg-warning' :
                          registro.status === AttendanceSheetStatusEnum.EM_CORRECAO ? 'bg-danger' :
                            registro.status === AttendanceSheetStatusEnum.AGUARDANDO_ASSINATURA ? 'bg-info' :
                              registro.status === AttendanceSheetStatusEnum.CONCLUIDA ? 'bg-success' : 'bg-secondary'
                          }`}>
                          {registro.status}
                        </span>
                        {!podeNotificar && <small className="ms-1">(Não notificável)</small>}
                      </td>
                      <td>
                        {foiEnviado && <span className="text-success">✅ Enviado</span>}
                        {erro && <span className="text-danger" title={erro.mensagem}>❌ Erro</span>}
                      </td>
                    </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={sendNotifications}
          disabled={selectedIds.length === 0 || isSending || carregando}
        >
          {isSending ? 'Enviando...' : `Notificar (${selectedIds.length})`}
        </Button>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSending}
        >
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}